import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Eye, EyeOff, Globe2, Layers, ShieldCheck } from 'lucide-react';
import PortalShell from '@/components/portal/PortalShell';
import { Button, EmptyState, Loading, UnavailableState } from '@/components/eds';
import MapViewer, { type MapLayerView } from '@/components/portal/gis/MapViewer';
import type { GeoClassification, GeoJsonFeatureCollection } from '@/services/gisService';
import { useAuth } from '@/contexts/AuthContext';
import { useInvestorGisEntitlement } from '@/hooks/useInvestorGisEntitlement';
import { investorGisService } from '@/services/investorGisService';
import {
  canExport,
  classifyFeatures,
  viewableLayers,
  type FeatureState,
  type InvestorGisLayer,
} from '@/lib/investorGis';

// R11P GEOLOGICAL P0.5-P9E — authenticated Investor GIS.
//
// Distinct from the anonymous Public GIS (/gis) and from the internal GIS
// Analyst workspace (/gis/dashboard). The catalogue, every operation and every
// feature read come from the server entitlement contract
// (GET /api/gis/investor/me/layers) — never from role, status, agreements,
// applications, classification, the URL or client state.
//
// SESSION ISOLATION: the workspace is keyed on the signed-in user id, so a
// logout or account change unmounts it and discards every layer, feature and
// map source; the entitlement hook also refuses any response that belongs to a
// previous identity.
//
// SOVEREIGN MAP: the P9C MapViewer is reused unchanged (MapLibre, overlays
// only, no third-party basemap/tile/geocoder). Features are loaded on demand
// per layer — nothing is fetched until the investor chooses a layer.

const MAX_FEATURE_ROWS = 25;

function featureStatusText(s: FeatureState | undefined, visible: boolean): string {
  if (!s) return visible ? 'Loading…' : 'Not loaded — show it on the map to load its features.';
  switch (s.kind) {
    case 'loading': return 'Loading features…';
    case 'ready': return `${s.features.length} feature${s.features.length === 1 ? '' : 's'} loaded.`;
    case 'empty': return 'This layer currently contains no features.';
    case 'denied': return 'Access to this layer’s features was not authorized. Its data has been cleared.';
    case 'auth_required': return 'Your session has ended. Please sign in again.';
    default: return 'Feature data is temporarily unavailable. This does not mean access has changed.';
  }
}

const InvestorGisWorkspace: React.FC = () => {
  const { snapshot, reload, markAuthLost } = useInvestorGisEntitlement();
  const [shown, setShown] = useState<Record<string, boolean>>({});
  const [features, setFeatures] = useState<Record<string, FeatureState>>({});
  const [exportMsg, setExportMsg] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');

  const viewable = useMemo(
    () => (snapshot.state === 'available' ? viewableLayers(snapshot.layers) : []),
    [snapshot],
  );

  // The loader reads the CURRENT server catalogue, never a stale closure.
  const viewableRef = useRef<InvestorGisLayer[]>(viewable);
  viewableRef.current = viewable;

  // Drop any restricted state for a layer that is no longer in the entitlement
  // (or for everything, when entitlement is no longer available).
  useEffect(() => {
    const ids = new Set(viewable.map((l) => l.id));
    const keep = <T,>(rec: Record<string, T>) =>
      Object.fromEntries(Object.entries(rec).filter(([id]) => ids.has(id)));
    setFeatures((f) => keep(f));
    setShown((s) => keep(s));
    setExportMsg((m) => keep(m));
  }, [viewable]);

  const featureLoader = useCallback(async (layerId: string): Promise<{
    data: GeoJsonFeatureCollection | null;
    error: string | null;
  }> => {
    setFeatures((f) => ({ ...f, [layerId]: { kind: 'loading' } }));
    const result = await investorGisService.getLayerFeatures(viewableRef.current, layerId);
    const state = classifyFeatures(result);
    setFeatures((f) => ({ ...f, [layerId]: state }));
    if (state.kind === 'auth_required') markAuthLost();
    if (state.kind === 'ready') {
      // The map receives GEOMETRY ONLY; attributes stay in the safe projection.
      return {
        data: {
          type: 'FeatureCollection',
          features: state.features.map((ft) => ({
            type: 'Feature' as const,
            geometry: ft.geometry,
            properties: null,
          })),
        },
        error: null,
      };
    }
    if (state.kind === 'empty') return { data: { type: 'FeatureCollection', features: [] }, error: null };
    return {
      data: null,
      error: state.kind === 'denied' ? 'Not authorized' : 'Temporarily unavailable',
    };
  }, [markAuthLost]);

  const mapLayers = useMemo<MapLayerView[]>(
    () => viewable.map((l) => ({
      id: l.id,
      name: l.name,
      category: '',
      permitted: true,
      classification: (l.classification ?? undefined) as GeoClassification | undefined,
      geometryType: l.geometryType ?? undefined,
      description: l.description,
      accessLevel: l.access === 'investor' ? 'Investor-authorized' : 'Public',
    })),
    [viewable],
  );

  // Explicit false for every layer: MapViewer treats a missing entry as
  // visible, which would eagerly load every authorized layer.
  const visibility = useMemo(
    () => Object.fromEntries(viewable.map((l) => [l.id, shown[l.id] === true])),
    [viewable, shown],
  );
  const onToggle = useCallback((id: string) => {
    setShown((s) => ({ ...s, [id]: !s[id] }));
  }, []);

  const onExport = useCallback(async (layer: InvestorGisLayer) => {
    if (!canExport(layer)) return;
    setExportMsg((m) => ({ ...m, [layer.id]: 'Preparing export…' }));
    const res = await investorGisService.exportLayer(layer);
    let msg = 'Export downloaded.';
    if (!res.ok) {
      if (res.status === 401) { markAuthLost(); msg = 'Your session has ended. Please sign in again.'; }
      else if (res.status === 403) { msg = 'Export was not authorized for this layer.'; reload(); }
      else msg = 'Export is temporarily unavailable.';
    }
    setExportMsg((m) => ({ ...m, [layer.id]: msg }));
  }, [markAuthLost, reload]);

  const q = filter.trim().toLowerCase();
  const listed = q
    ? viewable.filter((l) => l.name.toLowerCase().includes(q) || l.description.toLowerCase().includes(q))
    : viewable;

  const publicLink = (
    <Link
      to="/gis"
      className="font-semibold text-eds-emerald-2 underline underline-offset-2 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring rounded-eds-md"
    >
      Open Public GIS
    </Link>
  );

  let content: React.ReactNode;
  switch (snapshot.state) {
    case 'loading':
      content = <Loading label="Checking your Investor GIS access…" />;
      break;
    case 'unavailable':
      content = (
        <UnavailableState
          title="Investor GIS temporarily unavailable"
          message="Your Investor GIS access could not be confirmed right now. This does not mean your access has changed."
          action={<Button variant="secondary" size="sm" onClick={reload}>Try again</Button>}
        />
      );
      break;
    case 'auth_required':
      content = (
        <EmptyState
          title="Please sign in again"
          message="Your session has ended, so no Investor GIS information is shown."
          action={<Link to="/investor-login" className="font-semibold text-eds-emerald-2 underline">Sign in</Link>}
        />
      );
      break;
    case 'profile_required':
      content = (
        <EmptyState
          title="Investor profile required"
          message="Investor GIS is available only to accounts with an investor profile. Public GIS remains available to everyone."
          action={publicLink}
        />
      );
      break;
    case 'no_access':
      content = (
        <EmptyState
          title="No current Investor GIS access is available for this account."
          message="Public GIS remains separately available to everyone."
          action={publicLink}
        />
      );
      break;
    default:
      content = (
        <div className="space-y-6">
          <MapViewer
            layers={mapLayers}
            restrictedLayers={[]}
            visibility={visibility}
            onToggle={onToggle}
            featureLoader={featureLoader}
          />

          <section aria-labelledby="investor-gis-catalogue">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <h2 id="investor-gis-catalogue" className="font-eds-display text-lg font-semibold text-eds-fg flex items-center gap-2">
                <Layers className="w-5 h-5 text-eds-emerald-2" aria-hidden="true" /> Your layer catalogue
              </h2>
              <label className="flex flex-col gap-1 font-eds-body text-xs text-eds-muted-fg sm:w-72">
                Filter your authorized layers
                <input
                  type="search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="rounded-eds-md border border-eds-border bg-eds-surface px-3 py-2 text-sm text-eds-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
                />
              </label>
            </div>

            {listed.length === 0 && (
              <p className="mt-3 font-eds-body text-sm text-eds-muted-fg">No layers match this filter.</p>
            )}

            <ul className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
              {listed.map((l) => {
                const on = shown[l.id] === true;
                const fs = features[l.id];
                return (
                  <li key={l.id} className="min-w-0 rounded-eds-md border border-eds-border bg-eds-surface p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <h3 className="min-w-0 break-words font-eds-display font-semibold text-eds-fg">{l.name}</h3>
                      <span className="rounded-eds-sm border border-eds-border px-2 py-0.5 font-eds-body text-[11px] font-semibold uppercase tracking-wide text-eds-fg">
                        {l.access === 'investor' ? 'Investor access' : 'Public'}
                      </span>
                    </div>
                    {l.description && (
                      <p className="mt-1 font-eds-body text-sm text-eds-muted-fg break-words">{l.description}</p>
                    )}
                    <dl className="mt-2 font-eds-body text-xs text-eds-muted-fg space-y-0.5">
                      <div className="flex gap-1"><dt>Geometry:</dt><dd>{l.geometryType ?? 'GeoJSON'}</dd></div>
                      {l.classification && (
                        <div className="flex gap-1"><dt>Classification:</dt><dd>{l.classification}</dd></div>
                      )}
                      {l.access === 'investor' && l.expiresAt && (
                        <div className="flex gap-1"><dt>Access valid until:</dt><dd>{l.expiresAt.slice(0, 10)}</dd></div>
                      )}
                    </dl>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Button variant="secondary" size="sm" aria-pressed={on} onClick={() => onToggle(l.id)}>
                        {on ? <EyeOff className="mr-1 h-4 w-4" aria-hidden="true" /> : <Eye className="mr-1 h-4 w-4" aria-hidden="true" />}
                        {on ? `Hide ${l.name} from map` : `Show ${l.name} on map`}
                      </Button>
                      {canExport(l) && (
                        <Button variant="secondary" size="sm" onClick={() => void onExport(l)}>
                          <Download className="mr-1 h-4 w-4" aria-hidden="true" /> Export {l.name} (GeoJSON)
                        </Button>
                      )}
                    </div>

                    <p role="status" aria-live="polite" className="mt-2 font-eds-body text-xs text-eds-muted-fg">
                      {featureStatusText(fs, on)}
                      {exportMsg[l.id] ? ` ${exportMsg[l.id]}` : ''}
                    </p>

                    {fs?.kind === 'ready' && (
                      fs.features.some((ft) => ft.attributes.length > 0) ? (
                        <div className="mt-2 overflow-x-auto">
                          <table className="w-full font-eds-body text-xs text-eds-fg">
                            <caption className="sr-only">Feature details for {l.name}</caption>
                            <tbody>
                              {fs.features.slice(0, MAX_FEATURE_ROWS).map((ft, i) => (
                                <tr key={i} className="border-t border-eds-border align-top">
                                  <th scope="row" className="py-1 pr-2 text-left font-semibold">#{i + 1}</th>
                                  <td className="py-1">
                                    {ft.attributes.length
                                      ? ft.attributes.map((a) => `${a.label}: ${a.value}`).join(' · ')
                                      : 'No published attributes'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {fs.features.length > MAX_FEATURE_ROWS && (
                            <p className="mt-1 text-[11px] text-eds-muted-fg">
                              Showing {MAX_FEATURE_ROWS} of {fs.features.length} features.
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="mt-1 font-eds-body text-xs text-eds-muted-fg">
                          Geometry only — no investor-safe attributes are published for this layer.
                        </p>
                      )
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      );
  }

  return (
    <section className="py-8 md:py-12 bg-eds-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div>
          <h1 className="font-eds-display text-2xl font-semibold text-eds-fg">Investor GIS</h1>
          <p className="mt-1 font-eds-body text-sm text-eds-muted-fg">
            Spatial layers made available to your investor account.
          </p>
        </div>

        <div className="rounded-eds-md border border-eds-border bg-eds-surface-2 px-4 py-3 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-eds-emerald-2 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="font-eds-body text-sm text-eds-muted-fg">
            Additional geological and spatial information is made available only through
            GIISDEC-approved access, determined by the platform&apos;s authorization controls.
            Investor access does not make a layer public. Public GIS remains separately available.
          </p>
        </div>

        {content}

        <p className="flex items-center gap-2 font-eds-body text-sm text-eds-muted-fg">
          <Globe2 className="w-4 h-4" aria-hidden="true" />
          <span>Looking for openly published spatial information? {publicLink}.</span>
        </p>
      </div>
    </section>
  );
};

/** Keyed on the signed-in identity: an account change remounts with nothing retained. */
const InvestorGis: React.FC = () => {
  const { user } = useAuth();
  return (
    <PortalShell>
      <InvestorGisWorkspace key={user?.id ?? 'signed-out'} />
    </PortalShell>
  );
};

export default InvestorGis;
